/**
 * Una region, abierta desde su etiqueta en el mapa (ADM-10): su tarifa y sus
 * comunas con precio propio, que ya no tienen seccion aparte en la pagina.
 *
 * La tarifa se guarda sola, como antes cada fila de la tabla: corregir una
 * region no obliga a repasar las otras quince. Las comunas se guardan cada una
 * por su lado. Con comunas cargadas el modal se ensancha a dos columnas; sin
 * ellas queda angosto y solo ofrece agregar la primera.
 *
 * La lista de comunas va fuera del `<form>`: su buscador con Enter enviaria la
 * tarifa de la region.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import type { DespachoAdmin, TarifaComunaAdmin, TarifaRegionAdmin } from '@bandu/compartido';

import { guardarTarifaRegion } from '../../servicios/despacho-admin.servicio';
import { pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { ComunasRegion } from './ComunasRegion';
import { InterruptorGrande } from './InterruptorGrande';
import { Modal } from './Modal';

interface ModalTarifaRegionProps {
  /** `null` con el modal cerrado. */
  region: TarifaRegionAdmin | null;
  excepciones: TarifaComunaAdmin[];
  alGuardar: (datos: DespachoAdmin) => void;
  alAgregarComuna: (codigoRegion: string) => void;
  alEditarComuna: (excepcion: TarifaComunaAdmin) => void;
  alQuitarComuna: () => void;
  onCerrar: () => void;
}

export function ModalTarifaRegion({
  region,
  excepciones,
  onCerrar,
  ...resto
}: ModalTarifaRegionProps) {
  const propias =
    region === null
      ? []
      : excepciones.filter((excepcion) => excepcion.codigoRegion === region.codigo);

  return (
    <Modal
      titulo={region?.nombre ?? ''}
      bajada="Lo que cuesta llevar un pedido a esta region, cuanto demora y que comunas cobran distinto."
      abierto={region !== null}
      ancho={propias.length > 0 ? 'amplio' : 'normal'}
      onCerrar={onCerrar}
    >
      {/* Remontar por region: el formulario arranca siempre con lo guardado. */}
      {region !== null ? (
        <ContenidoRegion
          key={region.codigo}
          region={region}
          propias={propias}
          onCerrar={onCerrar}
          {...resto}
        />
      ) : null}
    </Modal>
  );
}

interface ContenidoRegionProps extends Omit<ModalTarifaRegionProps, 'region' | 'excepciones'> {
  region: TarifaRegionAdmin;
  propias: TarifaComunaAdmin[];
}

function ContenidoRegion({
  region,
  propias,
  alGuardar,
  alAgregarComuna,
  alEditarComuna,
  alQuitarComuna,
  onCerrar,
}: ContenidoRegionProps) {
  const [activa, setActiva] = useState(region.activa);
  const [costo, setCosto] = useState(region.costo === 0 ? '' : String(region.costo));
  const [plazo, setPlazo] = useState(region.plazo);
  const [guardando, setGuardando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  const costoNumero = Number(costo) || 0;

  async function guardar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setGuardando(true);
    setFalla(null);

    try {
      alGuardar(await guardarTarifaRegion(region.codigo, { costo: costoNumero, plazo, activa }));
      onCerrar();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
      setGuardando(false);
    }
  }

  return (
    <div className={propias.length > 0 ? 'modal-region con-comunas' : 'modal-region'}>
      <form
        className="modal-region-tarifa"
        onSubmit={(evento) => {
          void guardar(evento);
        }}
      >
        <InterruptorGrande
          encendido={activa}
          titulo="Despacho a esta region"
          detalleEncendido="Se puede elegir en el checkout."
          detalleApagado="No aparece en el checkout: nadie puede pedir despacho aca."
          alCambiar={setActiva}
        />

        <fieldset className="campos-region" disabled={!activa}>
          <legend className="solo-lectores">Tarifa</legend>

          <div className="campo">
            <label htmlFor="region-costo">Costo del despacho</label>
            <div className="entrada-pesos">
              <span aria-hidden="true">$</span>
              <input
                id="region-costo"
                className="entrada"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                placeholder="0"
                value={costo}
                onChange={(evento) => {
                  setCosto(evento.target.value);
                }}
              />
            </div>
            <p className={costoNumero === 0 ? 'campo-ayuda excedido' : 'campo-ayuda'}>
              {costoNumero === 0
                ? 'Sin precio, la region se ofrece gratis. Si no corresponde, carga el costo.'
                : `Se cobra ${pesos(costoNumero)} en toda la region, salvo las comunas con precio propio.`}
            </p>
          </div>

          <div className="campo">
            <label htmlFor="region-plazo">Plazo de entrega</label>
            <input
              id="region-plazo"
              className="entrada"
              type="text"
              maxLength={80}
              placeholder="2 a 3 dias habiles"
              value={plazo}
              onChange={(evento) => {
                setPlazo(evento.target.value);
              }}
            />
            <p className="campo-ayuda">Se muestra junto al costo en el checkout y en la ficha.</p>
          </div>
        </fieldset>

        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            {falla}
          </p>
        ) : null}

        <div className="modal-acciones">
          <button type="button" className="btn btn-contorno" onClick={onCerrar}>
            Cerrar
          </button>
          <button type="submit" className="btn btn-primario" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar tarifa'}
          </button>
        </div>
      </form>

      <ComunasRegion
        region={region}
        excepciones={propias}
        alAgregar={() => {
          alAgregarComuna(region.codigo);
        }}
        alEditar={alEditarComuna}
        alQuitar={alQuitarComuna}
      />
    </div>
  );
}
